import type { SearchLinks } from "@hobbyshopify/backend-client";
import { gradeKeyFromText, gradeLabel } from "../core/primitives/grades";

/**
 * eBay sold-comps links for a card, by grade.
 *
 * A scan's own `searchLinks` are opened verbatim: scan-core already appended the
 * affiliate params, and a link carrying them twice makes eBay bounce to sign-in.
 * The builder here is only the fallback for a grade the scan has no link for —
 * and a scan whose pricing failed has none at all, which is the scan a reviewer
 * most needs to look up. Same query flags, search terms and affiliate params as
 * scan-core's `pure/ebay-links.ts`; the query is the title, as the iOS
 * `AdminEbayLookup` builds it.
 */

const SOLD_SEARCH = "https://www.ebay.com/sch/i.html";
const AFFILIATE_PARAMS = "mkcid=1&mkrid=711-53200-19255-0&siteid=0&campid=5339106262&customid=&toolid=10001&mkevt=1";

function searchTerm(key: string): { term: string; graded: boolean } {
  if (key === "raw") return { term: "", graded: false };
  if (key === "bgs_10_black") return { term: "bgs+10+black+label", graded: true };
  if (key === "bgs_gold_label") return { term: "bgs+gold+label", graded: true };
  if (key === "sgc_gold_label") return { term: "sgc+gold+label", graded: true };
  if (key === "cgc_10_pristine") return { term: "cgc+10+pristine", graded: true };
  if (key === "cgc_10_perfect") return { term: "cgc+10+perfect", graded: true };
  const [company, ...grade] = key.split("_");
  return grade.length > 0 ? { term: `${company}+${grade.join(".")}`, graded: true } : { term: key, graded: true };
}

/** eBay's sold listings for a card at one grade, from its title; null without a title. */
export function soldSearchUrl(gradeKey: string, title: string): string | null {
  const name = title.trim();
  if (!name) return null;
  const { term, graded } = searchTerm(gradeKey);
  return (
    `${SOLD_SEARCH}?_nkw=${encodeURIComponent(name.toLowerCase())}${term ? `+${term}` : ""}` +
    `&LH_TitleDesc=1&LH_Complete=1&LH_Sold=1&rt=nc&Graded=${graded ? "Yes" : "No"}&_dcat=261328&${AFFILIATE_PARAMS}`
  );
}

/** The scan's own link for a grade, else one built from the title. */
export function gradeLinkUrl(
  gradeKey: string,
  searchLinks: SearchLinks | null | undefined,
  title: string,
): string | null {
  return searchLinks?.[gradeKey] || soldSearchUrl(gradeKey, title);
}

/**
 * The card's own grade as a key: its grade as written, else a key the scan
 * names for it (a grade set by hand, or the one its estimate was read from).
 * Null for a grade nobody can name, which the links treat like a raw card.
 */
export function ownGradeKey(
  gradeText: string | null | undefined,
  fallbackKey: string | null | undefined,
): string | null {
  const key = gradeKeyFromText(gradeText);
  if (key) return key;
  const fallback = fallbackKey?.trim();
  return fallback && gradeLabel(fallback) !== fallback ? fallback : null;
}

/**
 * The grades a review row links to: RAW, the card's own grade, and PSA 10 as the
 * ceiling to compare with — two when the card is raw or PSA 10 already, and PSA
 * 10 in second place for a card whose grade is unknown.
 */
export function quickLinkGrades(ownKey: string | null): string[] {
  const second = ownKey && ownKey !== "raw" ? ownKey : "psa_10";
  return second === "psa_10" ? ["raw", "psa_10"] : ["raw", second, "psa_10"];
}
